import { useQuery } from '@tanstack/react-query';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { discountsEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * Form-friendly response shape returned by the backend GET /discounts/:id endpoint.
 * Mirrors the admin portal discount form state (business logic mapping done server-side).
 */
export interface DiscountFormResponse {
	id: string;
	name: string;
	discountType: string;
	amountType: 'fixed' | 'percentage';
	method: 'code' | 'automatic';
	code: string;
	amount: number;
	appliesTo: 'all' | 'specific';
	applicableProducts: string[];
	startsAt: number;
	endsAt: number;
	hasEndDate: boolean;
}

/**
 * API function to fetch a single discount by ID
 */
export const getDiscount = async (
	servicesProviderId: string,
	discountId: string,
): Promise<DiscountFormResponse> => {
	const response = await apiClient.get<DiscountFormResponse>(
		discountsEndpoints.getDiscount(servicesProviderId, discountId),
	);
	return response.data;
};

/**
 * Hook for fetching a single discount by ID
 * Only enabled when both servicesProviderId and discountId are available
 */
export const useGetDiscount = (discountId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.discounts.details(servicesProviderId, discountId ?? '').queryKey,
		queryFn: () => getDiscount(servicesProviderId, discountId!),
		enabled: Boolean(servicesProviderId && discountId),
		staleTime: 5 * 60 * 1000, // 5 minutes
		...errorBoundaryOptions,
	});
};
